'use client';

import Icon from '@/components/ui/AppIcon';

interface PricingPlansProps {
  selectedPlan: string;
  onSelectPlan: (plan: string) => void;
}

export default function PricingPlans({ selectedPlan, onSelectPlan }: PricingPlansProps) {
  const plans = [
    {
      id: 'basic',
      name: 'Basic Ticket',
      price: 9.99,
      duration: '48 hours validity',
      features: [
        'Valid PNR for 48 hours',
        'Instant email delivery',
        'Airline verification',
        'Basic support'
      ],
      icon: 'TicketIcon',
      popular: false
    },
    {
      id: 'standard',
      name: 'Standard Ticket',
      price: 14.99,
      duration: '7 days validity',
      features: [
        'Valid PNR for 7 days',
        'Instant email delivery',
        'Airline & embassy verification',
        'Priority support',
        'Free date change (1x)'
      ],
      icon: 'DocumentCheckIcon',
      popular: true
    },
    {
      id: 'premium',
      name: 'Premium Ticket',
      price: 24.99,
      duration: '14 days validity',
      features: [
        'Valid PNR for 14 days',
        'Instant email delivery',
        'Airline & embassy verification',
        '24/7 priority support',
        'Free date changes (unlimited)',
        'Travel insurance included'
      ],
      icon: 'StarIcon',
      popular: false
    }
  ];

  return (
    <div className="bg-white rounded-2xl shadow-xl p-8">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-black text-gray-900 mb-2">Choose Your Plan</h2>
        <p className="text-gray-600">All prices in GBP (£) - Instant delivery within minutes</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map((plan) => (
          <div
            key={plan.id}
            onClick={() => onSelectPlan(plan.id)}
            className={`relative border-2 rounded-xl p-6 cursor-pointer transition-all duration-200 ${
              selectedPlan === plan.id
                ? 'border-blue-600 bg-blue-50 shadow-lg scale-105'
                : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
            } ${
              plan.popular ? 'ring-2 ring-blue-400 ring-offset-2' : ''
            }`}
          >
            {plan.popular && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-1 rounded-full text-xs font-bold">
                  MOST POPULAR
                </span>
              </div>
            )}

            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                <Icon name={plan.icon} size={32} className="text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-1">{plan.name}</h3>
              <p className="text-sm text-gray-500">{plan.duration}</p>
            </div>

            <div className="text-center mb-6">
              <div className="flex items-baseline justify-center">
                <span className="text-4xl font-black text-gray-900">£{plan.price}</span>
              </div>
            </div>

            <ul className="space-y-3 mb-6">
              {plan.features.map((feature, index) => (
                <li key={index} className="flex items-start gap-2">
                  <Icon name="CheckCircleIcon" size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">{feature}</span>
                </li>
              ))}
            </ul>

            <button
              className={`w-full py-3 rounded-lg font-bold transition-all duration-200 ${
                selectedPlan === plan.id
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {selectedPlan === plan.id ? 'Selected' : 'Select Plan'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}